// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import ProductDetailsPageBreadcrumbs from "@gcg/App/Components/ProductDetails/ProductDetailsPageBreadcrumbs";
import ResponsiveAccordion from "@gcg/App/Components/ResponsiveAccordion/ResponsiveAccordion";
import { zoneTitle } from "@gcg/App/SharedStyles/GeneralStyles";
import { screen } from "@gcg/XDL/Helpers";
import { printStyles } from "@gcg/XDL/Utilities";
import color from "@gcg/XDL/Utilities/color";
import { contentModeCookieName } from "@insite/client-framework/Common/ContentMode";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductDetailsViewStyles } from "@insite/content-library/Widgets/ProductDetails/ProductDetailsView";
import Hidden from "@insite/mobius/Hidden";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

export interface CustomProductDetailsViewStyles extends ProductDetailsViewStyles {
    breadcrumbsContainer?: InjectableCss;
    printShareContainer?: InjectableCss;
    row?: InjectableCss;
    imageColumn?: InjectableCss;
    infoColumn?: InjectableCss;
    detailsColumn?: InjectableCss;
    titleColumn?: InjectableCss;
    actionsColumn?: InjectableCss;
    productNumbersColumn?: InjectableCss;
    productInventoryColumn?: InjectableCss;
    actionsWrapper?: InjectableCss;
    productCarouselWrapper?: InjectableCss;
}

const columnPadding = 10;
const imageWidth = 450;
const actionsWidth = 350;
const imageColumnWidth = imageWidth + columnPadding * 2;
const actionsColumnWidth = actionsWidth + columnPadding * 2;

const columnStyle = css`
    float: left;
    padding: 0 ${columnPadding}px;

    ${screen("lte", "sm")`
        float: none;
        padding-left: 0;
        padding-right: 0;
    `}
`;

export const customViewStyles: CustomProductDetailsViewStyles = {
    container: {
        css: css`
            margin-top: 15px;
        `,
    },
    breadcrumbsContainer: {
        css: css`
            display: flex;
            justify-content: space-between;
            margin: 5px 0 15px 0;
        `,
    },
    printShareContainer: {
        css: css`
            display: flex;

            > div {
                margin-right: 30px;
                width: auto;

                &:last-child {
                    margin-right: 0;
                }
            }

            ${screen("lte", "sm")`
                margin: 10px 0 30px 0;
            `}
        `,
    },
    row: {
        css: css`
            display: block;
            margin: 0 -${columnPadding}px;
            &:before,
            &:after {
                content: "";
                display: block;
                width: 100%;
                clear: both;
            }

            ${screen("lte", "sm")`
                float: none;
                margin-left: 0;
                margin-right: 0;
            `}
        `,
    },
    imageColumn: {
        css: css`
            ${columnStyle};
            width: ${imageColumnWidth}px;

            ${screen("lte", "lg")`
                width: 50%;
            `}

            ${screen("lte", "sm")`
                order: 2;
                width: 100%;
                display: flex;
                justify-content: center;

                .pdp-image {
                    flex: 1 1 60%;
                    padding-right: 15px;
                    max-width: 60%;
                }
                
                .pdp-variant {
                    flex: 0 0 40%;
                    padding-left: 15px;
                    max-width: 40%;
                }
            `}

            ${screen("lte", "xs")`
                flex-wrap: wrap;

                .pdp-image {
                    flex: 1 1 100%;
                    padding-right: 0;
                    max-width: 100%;
                }
                
                .pdp-variant {
                    flex: 1 1 100%;
                    padding-left: 0;
                    max-width: 100%;
                }
            `}
        `,
    },
    infoColumn: {
        css: css`
            ${columnStyle};
            width: calc(100% - ${actionsColumnWidth}px);

            ${screen("lte", "md")`
                width: 100%;
                margin-bottom: 20px;
            `}

            ${screen("lte", "sm")`
                margin-bottom: 0;

                .info-container {
                    display: flex;
                    flex-direction: column;
                }
            `}
        `,
    },
    detailsColumn: {
        css: css`
            ${columnStyle};
            width: calc(100% - ${imageColumnWidth}px);
            float: right;

            ${screen("lte", "lg")`
                width: 50%;
            `}

            ${screen("lte", "sm")`
                width: 100%;
            `}
        `,
    },
    titleColumn: {
        css: css`
            ${columnStyle};
            width: calc(100% - ${imageColumnWidth}px);
            float: right;

            ${screen("lte", "lg")`
                width: 100%;
                float: left;
            `}

            ${screen("lte", "sm")`
                order: 0;
            `}
        `,
    },
    productNumbersColumn: {
        css: css`
            ${columnStyle};
            width: calc(50% - ${imageColumnWidth * 0.5}px);

            ${screen("lte", "lg")`
                width: 50%;
                margin-bottom: 30px;
            `}

            ${screen("lte", "sm")`
                order: 1;
                width: 100%;
                margin-bottom: 0;
            `}
        `,
    },
    productInventoryColumn: {
        css: css`
            ${columnStyle};
            float: right;
            width: calc(50% - ${imageColumnWidth * 0.5}px);

            ${screen("lte", "lg")`
                width: 50%;
            `}

            ${screen("lte", "sm")`
                order: 3;
                width: 100%;
            `}
        `,
    },
    actionsColumn: {
        css: css`
            ${columnStyle};
            width: ${actionsColumnWidth}px;
            margin-top: 15px;

            ${screen("lte", "md")`
                width: 100%;
                max-width: ${actionsColumnWidth}px;
                margin: 20px auto;
                float: none;
                clear: both;
            `}

            ${screen("lte", "sm")`
                margin: 0;
                max-width: none;
            `}

            ${printStyles.hideOnPrint}
        `,
    },
    actionsWrapper: {
        css: css`
            padding: 20px;
            background-color: ${getColor("common.ui3")};
            border-radius: 20px;
            box-shadow: 0 12px 24px 0 ${color("secondary.main", 0.1)};

            ${screen("lte", "sm")`
                display: flex;
                flex-wrap: wrap;
                justify-content: space-between;
                align-items: center;
                background: none;
                box-shadow: none;
                border-radius: 0;
                padding: 0;

                > div {
                    width: auto;
                }
            `}

            ${printStyles.hideOnPrint}
        `,
    },
    productCarouselWrapper: {
        css: printStyles.hideOnPrint,
    },
};

const styles = customViewStyles;

const ProductDetailsView: React.FC<WidgetProps & HasProduct> = props => {
    const isCmsEditing = getCookie(contentModeCookieName) === "Editing";
    const isVariant = props.product.variantTraits && props.product.variantTraits.length > 0;

    return (
        <>
            <StyledWrapper {...styles.breadcrumbsContainer}>
                <ProductDetailsPageBreadcrumbs hideCurrentPage={true} />
                <Hidden below="lg">
                    {isCmsEditing && <Typography {...zoneTitle}>Share & Print Zone</Typography>}
                    <StyledWrapper {...styles.printShareContainer}>
                        <Zone contentId={props.id} zoneName="Share Zone" />
                        <Zone contentId={props.id} zoneName="Print Zone" />
                    </StyledWrapper>
                </Hidden>
            </StyledWrapper>
            <StyledWrapper>
                {isCmsEditing && <Typography {...zoneTitle}>Banner Zone</Typography>}
                <Zone contentId={props.id} zoneName="Banner Zone" />
            </StyledWrapper>
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.row}>
                    <StyledWrapper {...styles.infoColumn}>
                        <StyledWrapper className="info-container" {...styles.row}>
                            <StyledWrapper {...styles.titleColumn}>
                                {isCmsEditing && <Typography {...zoneTitle}>Title Zone</Typography>}
                                <Zone contentId={props.id} zoneName="Title Zone" />
                            </StyledWrapper>
                            <StyledWrapper {...styles.imageColumn}>
                                {isCmsEditing && <Typography {...zoneTitle}>Image Zone</Typography>}
                                <StyledWrapper className="pdp-image">
                                    <Zone contentId={props.id} zoneName="Image Zone" />
                                </StyledWrapper>
                                {isVariant && (
                                    <Hidden className="pdp-variant" above="sm">
                                        <Zone contentId={props.id} zoneName="Variants Zone" />
                                    </Hidden>
                                )}
                            </StyledWrapper>
                            <StyledWrapper {...styles.productNumbersColumn}>
                                {isCmsEditing && <Typography {...zoneTitle}>Product Numbers Zone</Typography>}
                                <ResponsiveAccordion breakpoint="sm">
                                    <Zone contentId={props.id} zoneName="Product Numbers Zone" />
                                </ResponsiveAccordion>
                            </StyledWrapper>
                            <StyledWrapper {...styles.productInventoryColumn}>
                                {isCmsEditing && <Typography {...zoneTitle}>Inventory Zone</Typography>}
                                <Zone contentId={props.id} zoneName="Inventory Zone" />
                            </StyledWrapper>
                            <Hidden {...styles.productInventoryColumn} below="md">
                                {isCmsEditing && <Typography {...zoneTitle}>Badges Zone</Typography>}
                                <Zone contentId={props.id} zoneName="Badges Zone" />
                            </Hidden>
                            <Hidden below="lg">
                                <StyledWrapper {...styles.detailsColumn}>
                                    {isCmsEditing && <Typography {...zoneTitle}>Product Description Zone</Typography>}
                                    <Zone contentId={props.id} zoneName="Details Zone" />
                                </StyledWrapper>
                            </Hidden>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.actionsColumn}>
                        <StyledWrapper {...styles.actionsWrapper}>
                            {isCmsEditing && <Typography {...zoneTitle}>Price Zone</Typography>}
                            <Zone contentId={props.id} zoneName="Price Zone" />
                            <Hidden below="md">
                                {isCmsEditing && <Typography {...zoneTitle}>Variants Zone</Typography>}
                                <Zone contentId={props.id} zoneName="Variants Zone" />
                            </Hidden>
                            {isCmsEditing && <Typography {...zoneTitle}>Actions Zone</Typography>}
                            <Zone contentId={props.id} zoneName="Actions Zone" />
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                <Hidden {...styles.productInventoryColumn} above="sm">
                    <Zone contentId={props.id} zoneName="Badges Zone" />
                </Hidden>
                <Hidden above="md">
                    <Zone contentId={props.id} zoneName="Details Zone" />
                    <StyledWrapper {...styles.printShareContainer}>
                        <Zone contentId={props.id} zoneName="Share Zone" />
                        <Zone contentId={props.id} zoneName="Print Zone" />
                    </StyledWrapper>
                </Hidden>
                <StyledWrapper>
                    {isCmsEditing && <Typography {...zoneTitle}>Tabs Zone</Typography>}
                    <Zone contentId={props.id} zoneName="Tabs Zone" />
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.productCarouselWrapper}>
                {isCmsEditing && <Typography {...zoneTitle}>Product Carousels Zone</Typography>}
                <Zone contentId={props.id} zoneName="Product Carousels Zone" />
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsView),
    definition: {
        displayName: "View",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
